import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Paper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import SitesService from '../../services/SitesService';
import SitesOrderService, { SITES_ORDER_DISPLAY_OFFSET } from '../../services/SitesOrderService';
import PageHeader from '../../components/PageHeader/PageHeader';
import TableRefreshButton from '../../components/TableRefreshButton/TableRefreshButton';
import OrderDetailModal from '../sites/orders/OrderDetailModal';
import SnackbarUtils from '../../utils/SnackbarUtils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

dayjs.extend(relativeTime);
dayjs.locale('es');

const AllNotifications = () => {
  const { t } = useTranslation();

  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isReadFilter, setIsReadFilter] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const searchTimeoutRef = useRef(null);

  // Estados para el modal de detalles de orden
  const [orderDetail, setOrderDetail] = useState(null);
  const [isOrderDetailModalOpen, setIsOrderDetailModalOpen] = useState(false);
  const [loadingOrderDetail, setLoadingOrderDetail] = useState(false);

  const fetchNotifications = useCallback(async (page, pageSize, search, type, isRead) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await SitesService.getNotifications(
        type || null,
        isRead !== null && isRead !== '' ? isRead : null,
        page,
        pageSize,
        search || null // Pasar el término de búsqueda al backend
      );

      let notificationsList = [];
      if (response && typeof response === 'object') {
        notificationsList = response.notifications || response.data || [];
        setTotalRows(response.total || response.totalCount || 0);
      } else {
        notificationsList = response || [];
        setTotalRows(response?.length || 0);
      }

      setNotifications(notificationsList);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.message || 'Error al cargar notificaciones');
      setNotifications([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce para el campo de búsqueda
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Cargar datos cuando cambian los filtros o la paginación
  useEffect(() => {
    fetchNotifications(
      paginationModel.page + 1,
      paginationModel.pageSize,
      debouncedSearchTerm,
      typeFilter,
      isReadFilter
    );
  }, [fetchNotifications, paginationModel.page, paginationModel.pageSize, debouncedSearchTerm, typeFilter, isReadFilter]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleIsReadFilterChange = (event) => {
    setIsReadFilter(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'contact':
        return 'primary';
      case 'career':
        return 'success';
      case 'newsletter':
        return 'info';
      case 'order':
        return 'warning';
      case 'billing':
        return 'error';
      case 'b2b':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'contact':
        return t('contact') || 'Contacto';
      case 'career':
        return t('career') || 'Carrera';
      case 'newsletter':
        return t('newsletter') || 'Newsletter';
      case 'order':
        return t('order') || 'Orden';
      case 'billing':
        return t('billing') || 'Facturación';
      case 'b2b':
        return 'B2B';
      default:
        return type || '-';
    }
  };

  const extractOrderIdFromDescription = (description) => {
    const match = description?.match(/Orden #(\d+)/);
    if (!match) return null;
    const parsed = parseInt(match[1], 10);
    return parsed >= SITES_ORDER_DISPLAY_OFFSET ? parsed - SITES_ORDER_DISPLAY_OFFSET : parsed;
  };

  const handleView = async (notification) => {
    if (notification.type === 'order') {
      const orderId = extractOrderIdFromDescription(notification.description);
      if (orderId) {
        try {
          setLoadingOrderDetail(true);
          const order = await SitesOrderService.getOrderById(orderId);
          setOrderDetail(order);
          setIsOrderDetailModalOpen(true);

          if (!notification.isRead) {
            try {
              await SitesService.markNotificationAsRead(notification.id);
              fetchNotifications(
                paginationModel.page + 1,
                paginationModel.pageSize,
                debouncedSearchTerm,
                typeFilter,
                isReadFilter
              );
            } catch (err) {
              console.error('Error marking notification as read:', err);
            }
          }
        } catch (err) {
          console.error('Error loading order details:', err);
          SnackbarUtils.error(t('error_loading_order_details') || 'Error al cargar los detalles del pedido');
        } finally {
          setLoadingOrderDetail(false);
        }
        return;
      }
    }

    setSelectedNotification(notification);
    setIsViewModalOpen(true);

    if (!notification.isRead) {
      try {
        await SitesService.markNotificationAsRead(notification.id);
        fetchNotifications(
          paginationModel.page + 1,
          paginationModel.pageSize,
          debouncedSearchTerm,
          typeFilter,
          isReadFilter
        );
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await SitesService.markAllNotificationsAsRead();
      window.dispatchEvent(new CustomEvent('notifications-all-read'));
      fetchNotifications(
        paginationModel.page + 1,
        paginationModel.pageSize,
        debouncedSearchTerm,
        typeFilter,
        isReadFilter
      );
      SnackbarUtils.success(t('all_notifications_marked_as_read') || 'Todas las notificaciones fueron marcadas como leídas');
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      SnackbarUtils.error(err.response?.data?.message || 'Error al marcar todas las notificaciones como leídas');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD/MM/YYYY HH:mm');
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).fromNow();
  };

  const columns = [
    {
      field: 'type',
      headerName: t('type') || 'Tipo',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getTypeLabel(params.value)}
          size="small"
          color={getTypeColor(params.value)}
          sx={{ height: 24, fontSize: '0.75rem' }}
        />
      ),
    },
    {
      field: 'title',
      headerName: t('title') || 'Título',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'description',
      headerName: t('description') || 'Descripción',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.value || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isRead',
      headerName: t('status') || 'Estado',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('read') || 'Leída' : t('unread') || 'No leída'}
          size="small"
          color={params.value ? 'default' : 'primary'}
          variant={params.value ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: t('date') || 'Fecha',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2">{formatDate(params.value)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTimeAgo(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: t('actions') || 'Acciones',
      width: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Tooltip title={t('view') || 'Ver'}>
            <IconButton
              size="small"
              onClick={() => handleView(params.row)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                backgroundColor: 'action.hover',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              }}
            >
              <ion-icon name="eye-outline" style={{ fontSize: '20px' }}></ion-icon>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box p={3}>
      <PageHeader
        title={t('all_notifications') || 'Todas las Notificaciones'}
        subtitle={t('all_notifications_subtitle') || 'Gestiona todas las notificaciones del sistema'}
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                label={t('search') || 'Buscar'}
                variant="outlined"
                value={searchTerm}
                onChange={handleSearchChange}
                size="small"
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('type') || 'Tipo'}</InputLabel>
                <Select value={typeFilter} onChange={handleTypeFilterChange} label={t('type') || 'Tipo'}>
                  <MenuItem value="">{t('all') || 'Todos'}</MenuItem>
                  <MenuItem value="order">{t('order') || 'Orden'}</MenuItem>
                  <MenuItem value="contact">{t('contact') || 'Contacto'}</MenuItem>
                  <MenuItem value="career">{t('career') || 'Carrera'}</MenuItem>
                  <MenuItem value="newsletter">{t('newsletter') || 'Newsletter'}</MenuItem>
                  <MenuItem value="billing">{t('billing') || 'Facturación'}</MenuItem>
                  <MenuItem value="b2b">B2B</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('status') || 'Estado'}</InputLabel>
                <Select value={isReadFilter} onChange={handleIsReadFilterChange} label={t('status') || 'Estado'}>
                  <MenuItem value="">{t('all') || 'Todos'}</MenuItem>
                  <MenuItem value="false">{t('unread') || 'No leídas'}</MenuItem>
                  <MenuItem value="true">{t('read') || 'Leídas'}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <TableRefreshButton
                loading={loading}
                onClick={() =>
                  fetchNotifications(
                    paginationModel.page + 1,
                    paginationModel.pageSize,
                    debouncedSearchTerm,
                    typeFilter,
                    isReadFilter
                  )
                }
              />
              <Button
                variant="contained"
                onClick={handleMarkAllAsRead}
                sx={{ 
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content',
                  px: 2,
                  fontSize: '0.875rem'
                }}
              >
                {t('mark_all_as_read') || 'Marcar todas como leídas'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabla de notificaciones */}
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={notifications}
            columns={columns}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 20, 50, 100]}
            rowCount={totalRows}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Modal Ver Detalles */}
      <Dialog
        open={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedNotification(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('notification_details') || 'Detalles de la Notificación'}</Typography>
            {selectedNotification?.type && (
              <Chip
                label={getTypeLabel(selectedNotification.type)}
                size="small"
                color={getTypeColor(selectedNotification.type)}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('title') || 'Título'}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {selectedNotification.title}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('description') || 'Descripción'}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedNotification.description}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('date') || 'Fecha'}
                </Typography>
                <Typography variant="body1">
                  {formatDate(selectedNotification.createdAt)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsViewModalOpen(false);
              setSelectedNotification(null);
            }}
          >
            {t('close') || 'Cerrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de detalles de orden */}
      <OrderDetailModal
        open={isOrderDetailModalOpen}
        onClose={() => {
          setIsOrderDetailModalOpen(false);
          setOrderDetail(null);
        }}
        order={orderDetail}
      />
    </Box>
  );
};

export default AllNotifications;
